import { isSameDay, isSameYear } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { tz } from '@date-fns/tz';
import type { IntlFormatters } from 'react-intl';

import { DateSalesWithTz } from '@eventbrite/event-listing-service-client';
import { getFormattedDateTime } from '@eventbrite/legacy-infra.datetime-fns';

import { BasicInfo } from '@/types';

export const DEFAULT_TZ = 'UTC';
export const FULL_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";

type FormatMessage = IntlFormatters['formatMessage'];

export type DateObject = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  dayPeriod: string;
  timeZoneName: string;
};

export type MonthFormatType = 'long' | 'short';
export type WeekdayFormatType = 'long' | 'short' | null;

export interface DateFormatOptions {
  locale: string;
  timezone: string;
  monthFormat?: MonthFormatType;
  showYear?: boolean;
  weekday?: WeekdayFormatType;
}

export const getAttendeeTimezone = (): string | undefined => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e: unknown) {
    console.error('Error getting attendee timezone from browser', e);
    return undefined;
  }
};

export const getDisplayTimezone = ({
  isOnlineEvent,
  eventTimezone,
  attendeeTimezone,
}: {
  isOnlineEvent: boolean;
  eventTimezone?: string | null;
  attendeeTimezone?: string;
}): string => {
  const normalizedEventTimezone = eventTimezone?.trim() ? eventTimezone : undefined;
  const normalizedAttendeeTimezone = attendeeTimezone?.trim() ? attendeeTimezone : undefined;

  if (isOnlineEvent) {
    return normalizedAttendeeTimezone ?? normalizedEventTimezone ?? DEFAULT_TZ;
  }

  return normalizedEventTimezone ?? DEFAULT_TZ;
};

export const isSameDayInTimezone = (utcString1: string, utcString2: string, timezone: string): boolean => {
  return isSameDay(utcString1, utcString2, {
    in: tz(timezone),
  });
};

const isCurrentYear = (utcString: string, locale: string, timezone: string): boolean => {
  return isSameYear(utcString, new Date().toLocaleString(locale, { timeZone: timezone }), {
    in: tz(timezone),
  });
};
const extractTimezoneFromFormatted = (formatted: string): string | null => {
  const match = formatted.match(/\s([A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5})$/);
  return match?.[1] || null;
};

const extractTimezoneFromIntl = (utcString: string, locale: string, timezone: string): string => {
  const timeZonePart = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    timeZone: timezone,
  })
    .formatToParts(new Date(utcString))
    .find((part) => part.type === 'timeZoneName');

  if (!timeZonePart?.value) {
    return '';
  }

  const abbrevMatch = timeZonePart.value.match(/[A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5}/);
  return abbrevMatch ? abbrevMatch[0] : timeZonePart.value.toUpperCase().slice(0, 5);
};

const extractTimezoneAbbreviation = (utcString: string, locale: string, timezone: string): string => {
  const formatted = getFormattedDateTime(utcString, true, timezone, locale, 'p');
  const fromFormatted = extractTimezoneFromFormatted(formatted);

  if (fromFormatted) {
    return fromFormatted;
  }

  try {
    return extractTimezoneFromIntl(utcString, locale, timezone);
  } catch (error) {
    console.error('Error extracting timezone abbreviation', error);
    return '';
  }
};

const normalizeTimeFormat = (formatted: string): string =>
  formatted
    .replace(/(AM|PM)/g, (match) => match.toLowerCase())
    .replace(/:00\s*(am|pm)/g, ' $1')
    .replace(/(\d)(am|pm)/g, '$1 $2');

const compactDayPeriod = (dayPeriod: string): string => dayPeriod.replace(/\s/g, '').toLowerCase();

const formatTimeFromParts = (hour: string, minute: string, dayPeriod: string): string => {
  const period = compactDayPeriod(dayPeriod);
  return minute === '00' ? `${hour}${period}` : `${hour}:${minute}${period}`;
};

const extractTimeFromFormatted = (formatted: string): string => {
  const timeMatch = formatted.match(/(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm))/i);
  return timeMatch
    ? normalizeTimeFormat(timeMatch[1])
    : normalizeTimeFormat(formatted.replace(/\s([A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5})$/, ''));
};

const formatTimeOnly = (utcString: string, locale: string, timezone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hourCycle: 'h12',
      timeZone: timezone,
    }).formatToParts(new Date(utcString));

    const getPart = (type: string) => parts.find((p) => p.type === type)?.value || '';
    const hour = getPart('hour');
    const minute = getPart('minute');
    const dayPeriod = getPart('dayPeriod');

    if (hour && minute && dayPeriod) {
      return formatTimeFromParts(hour, minute, dayPeriod);
    }
  } catch (error) {
    console.error('Error formatting time', error);
  }

  const formatted = getFormattedDateTime(utcString, true, timezone, locale, 'p');
  return extractTimeFromFormatted(formatted);
};

const createAtSeparator = (formatMessage: FormatMessage): string =>
  `'${formatMessage({ id: 'at', defaultMessage: 'at', description: 'Date joiner from day to hour' })}'`;

export const getFormattedDateForSingleDay = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  includeWeekday: boolean = true,
): string => {
  if (!date.utc) {
    return '';
  }

  const addYear = !isCurrentYear(date.utc, locale, timezone);
  const atSeparator = createAtSeparator(formatMessage);
  const datePart = includeWeekday ? `eeee, MMM d` : `MMM d`;
  const format = `${datePart}${addYear ? ', yyyy' : ''} ${atSeparator} p`;

  return normalizeTimeFormat(getFormattedDateTime(date.utc, true, timezone, locale, format));
};

export const getFormattedDateOnly = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc) {
    return '';
  }
  const addYear = !isCurrentYear(date.utc, locale, timezone);
  return getFormattedDateTime(date.utc, false, timezone, locale, `eeee, MMM d${addYear ? ', yyyy' : ''}`);
};

const getFormattedDateWithTimeIntl = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  dateTimeSeparator?: string,
  includeWeekday?: boolean,
  showTimezone: boolean = true,
): string => {
  if (!date.utc) return '';
  const separator =
    dateTimeSeparator ??
    formatMessage({
      id: 'at',
      defaultMessage: 'at',
      description: 'Date joiner from day to hour',
    });
  const dateFormatted = includeWeekday
    ? formatDateWithWeekday(date, locale, timezone)
    : formatDateWithoutWeekday(date, locale, timezone);
  const timeFormatted = formatTimeOnly(date.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(date.utc, locale, timezone);
  return normalizeTimeFormat(
    `${dateFormatted} ${separator} ${timeFormatted}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`,
  );
};

export const getSingleDateDisplayString = (
  date: DateSalesWithTz,
  showTime: boolean,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  includeWeekday: boolean = true,
  dateTimeSeparator?: string,
  useAbbreviatedWeekday?: boolean,
  showTimezone: boolean = true,
): string => {
  if (!date.utc) {
    return '';
  }
  if (useAbbreviatedWeekday) {
    const datePart = formatDateWithAbbreviatedWeekday(date, locale, timezone);
    if (!showTime) return datePart;
    const separator =
      dateTimeSeparator ??
      formatMessage({ id: 'at', defaultMessage: 'at', description: 'Date joiner from day to hour' });
    const timeFormatted = formatTimeOnly(date.utc, locale, timezone);
    const timezoneAbbrev = extractTimezoneAbbreviation(date.utc, locale, timezone);
    return normalizeTimeFormat(
      `${datePart} ${separator} ${timeFormatted}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`,
    );
  }
  if (showTime) {
    if (!includeWeekday) {
      return getFormattedDateWithTimeIntl(
        date,
        locale,
        timezone,
        formatMessage,
        dateTimeSeparator,
        undefined,
        showTimezone,
      );
    }
    return getFormattedDateForSingleDay(date, locale, timezone, formatMessage, includeWeekday);
  }
  return includeWeekday
    ? getFormattedDateOnly(date, locale, timezone)
    : formatDateWithoutWeekday(date, locale, timezone);
};

const formatTimezoneIfPresent = (timezone: string, show: boolean = true): string =>
  show && timezone ? ` ${timezone}` : '';

export const getFormattedDateForSameDayWithTimeRange = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) {
    return '';
  }

  const dateFormatted = formatDateWithWeekday(startDate, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(startDate.utc, locale, timezone);
  const from = formatMessage({ id: 'from', defaultMessage: 'from', description: 'Date joiner from day to time range' });
  const to = formatMessage({
    id: 'to',
    defaultMessage: 'to',
    description: 'Joiner word connecting two time or date values',
  });

  const startTime = formatTimeOnly(startDate.utc, locale, timezone);
  const endTime = formatTimeOnly(endDate.utc, locale, timezone);

  return normalizeTimeFormat(
    `${dateFormatted} ${from} ${startTime} ${to} ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`,
  );
};

const removeTimezoneFromFormatted = (str: string): string =>
  normalizeTimeFormat(str.replace(/\s([A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5})$/, ''));

export const formatDateWithoutWeekday = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc) return '';
  const addYear = !isCurrentYear(date.utc, locale, timezone);
  return getFormattedDateTime(date.utc, false, timezone, locale, `MMM d${addYear ? ', yyyy' : ''}`);
};

const formatDateWithWeekday = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc) return '';
  const addYear = !isCurrentYear(date.utc, locale, timezone);
  return getFormattedDateTime(date.utc, false, timezone, locale, `eeee, MMM d${addYear ? ', yyyy' : ''}`);
};

export const formatDateWithAbbreviatedWeekday = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc) return '';
  const addYear = !isCurrentYear(date.utc, locale, timezone);
  return getFormattedDateTime(date.utc, false, timezone, locale, `EEE, MMM d${addYear ? ', yyyy' : ''}`);
};

const getFormattedDateRangeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
): string => {
  if (!startDate.utc || !endDate.utc) return '';
  const toSeparator = formatMessage({
    id: 'to',
    defaultMessage: 'to',
    description: 'Joiner word connecting two time or date values',
  });
  const startFormatted = formatDateWithWeekday(startDate, locale, timezone);
  const endFormatted = formatDateWithWeekday(endDate, locale, timezone);
  return `${startFormatted} ${toSeparator} ${endFormatted}`;
};

export const getFormattedDateEndsAt = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!date.utc) return '';
  const endsAt = formatMessage({
    id: 'ends at',
    defaultMessage: 'ends at',
    description: 'Label before end time when start time is hidden',
  });
  const dateFormatted = formatDateWithWeekday(date, locale, timezone);
  const endTime = formatTimeOnly(date.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(date.utc, locale, timezone);
  return `${dateFormatted} ${endsAt} ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

const getFormattedDateRangeWithEndTimeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) return '';
  const toSeparator = formatMessage({
    id: 'to',
    defaultMessage: 'to',
    description: 'Joiner word connecting two time or date values',
  });
  const endsAt = formatMessage({
    id: 'ends at',
    defaultMessage: 'ends at',
    description: 'Label before end time when start time is hidden',
  });
  const startFormatted = formatDateWithWeekday(startDate, locale, timezone);
  const endFormatted = formatDateWithWeekday(endDate, locale, timezone);
  const endTime = formatTimeOnly(endDate.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(endDate.utc, locale, timezone);
  return `${startFormatted} ${toSeparator} ${endFormatted} ${endsAt} ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

const getFormattedDateRangeWithStartTimeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) return '';
  const atSeparator = formatMessage({
    id: 'at',
    defaultMessage: 'at',
    description: 'Date joiner from day to hour',
  });
  const toSeparator = formatMessage({
    id: 'to',
    defaultMessage: 'to',
    description: 'Joiner word connecting two time or date values',
  });
  const startFormatted = formatDateWithWeekday(startDate, locale, timezone);
  const endFormatted = formatDateWithWeekday(endDate, locale, timezone);
  const startTime = formatTimeOnly(startDate.utc, locale, timezone);
  const timezoneAbbrev = extractTimezoneAbbreviation(startDate.utc, locale, timezone);
  return `${startFormatted} ${atSeparator} ${startTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)} ${toSeparator} ${endFormatted}`;
};

const formatDateWithTime = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  atSeparator: string,
  addYear: boolean,
  includeWeekday: boolean,
): string => {
  const datePart = includeWeekday ? `eeee, MMM d` : `MMM d`;
  return getFormattedDateTime(
    date.utc!,
    true,
    timezone,
    locale,
    `${datePart}${addYear ? ', yyyy' : ''} ${atSeparator} p`,
  );
};

export const getFormattedDateForTwoDays = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if (!startDate.utc || !endDate.utc) {
    return '';
  }

  const addYearToStart = !isCurrentYear(startDate.utc, locale, timezone);
  const addYearToEnd = !isCurrentYear(endDate.utc, locale, timezone);
  const atSeparator = createAtSeparator(formatMessage);

  const startFormatted = formatDateWithTime(startDate, locale, timezone, atSeparator, addYearToStart, true);
  const endFormatted = formatDateWithTime(endDate, locale, timezone, atSeparator, addYearToEnd, true);
  const timezoneAbbrev = extractTimezoneAbbreviation(startDate.utc, locale, timezone);

  const toSeparator = formatMessage({
    id: 'to',
    defaultMessage: 'to',
    description: 'Joiner word connecting two time or date values',
  });

  return `${removeTimezoneFromFormatted(startFormatted)} ${toSeparator} ${removeTimezoneFromFormatted(endFormatted)}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

const EMPTY_END_DATE: DateSalesWithTz = { utc: null, timezone: undefined } as unknown as DateSalesWithTz;

export type GetEventDateTimeDisplayStringParams = {
  startDate: DateSalesWithTz;
  endDate: DateSalesWithTz;
  hideStartDate: boolean;
  hideEndDate: boolean;
  locale: string;
  timezone: string;
  formatMessage: FormatMessage;
  isRecurringEvent: boolean;
  recurringEventLabel?: string;
  showTimezone?: boolean;
};

export const getEventDateTimeDisplayString = (params: GetEventDateTimeDisplayStringParams): string | null => {
  const {
    startDate,
    endDate: rawEndDate,
    hideStartDate,
    hideEndDate,
    locale,
    timezone,
    formatMessage,
    isRecurringEvent,
    recurringEventLabel,
    showTimezone = true,
  } = params;

  const endDate = rawEndDate ?? EMPTY_END_DATE;

  if (!startDate.utc) {
    return null;
  }

  if (isRecurringEvent && recurringEventLabel) {
    return recurringEventLabel;
  }

  if (hideStartDate && hideEndDate) {
    if (endDate.utc && !isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
      return getFormattedDateRangeOnly(startDate, endDate, locale, timezone, formatMessage);
    }
    return formatDateWithWeekday(startDate, locale, timezone);
  }

  if (!hideStartDate && hideEndDate) {
    if (endDate.utc && !isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
      return getFormattedDateRangeWithStartTimeOnly(startDate, endDate, locale, timezone, formatMessage, showTimezone);
    }
    return getFormattedDateWithTimeIntl(startDate, locale, timezone, formatMessage, undefined, true, showTimezone);
  }

  if (hideStartDate && !hideEndDate) {
    if (!endDate.utc) {
      return formatDateWithWeekday(startDate, locale, timezone);
    }
    if (isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
      return getFormattedDateEndsAt(endDate, locale, timezone, formatMessage, showTimezone);
    }
    return getFormattedDateRangeWithEndTimeOnly(startDate, endDate, locale, timezone, formatMessage, showTimezone);
  }

  if (!endDate.utc) {
    return getFormattedDateWithTimeIntl(startDate, locale, timezone, formatMessage, undefined, true, showTimezone);
  }

  if (isSameDayInTimezone(startDate.utc, endDate.utc, timezone)) {
    return getFormattedDateForSameDayWithTimeRange(startDate, endDate, locale, timezone, formatMessage, showTimezone);
  }

  return getFormattedDateForTwoDays(startDate, endDate, locale, timezone, formatMessage, showTimezone);
};

export const getFormattedStartDate = (basicInfo: BasicInfo) => {
  if (!basicInfo.hideStartDate && basicInfo.startDate.utc) {
    return formatInTimeZone(
      new Date(basicInfo.startDate.utc),
      basicInfo.startDate.timezone || DEFAULT_TZ,
      FULL_DATE_TIME_FORMAT,
    );
  }
  return null;
};

export const getFormattedEndDate = (basicInfo: BasicInfo) => {
  if (!basicInfo.hideEndDate && basicInfo.endDate?.utc) {
    return formatInTimeZone(
      new Date(basicInfo.endDate.utc),
      basicInfo.endDate.timezone || DEFAULT_TZ,
      FULL_DATE_TIME_FORMAT,
    );
  }
  return null;
};

export const getFormattedDateOnlyLocaleBased = (dateObj: Date, options: DateFormatOptions): string => {
  const { locale, timezone, monthFormat = 'long', showYear = false, weekday = null } = options;

  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: monthFormat,
    timeZone: timezone,
  };

  if (showYear) {
    formatOptions.year = 'numeric';
  }

  if (weekday) {
    formatOptions.weekday = weekday;
  }

  const formatter = new Intl.DateTimeFormat(locale, formatOptions);
  return formatter.format(dateObj);
};
